import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import vibrantLogo from '../../assets/integrations/vibrant_logo.png';
import microbiologydxLogo from '../../assets/integrations/microbiologydx_logo.png';
import janeAppLogo from '../../assets/integrations/jane_app_logo.png';
import rupaLogo from '../../assets/integrations/rupa_logo.jpeg';
import {
  explorePortal, getExploreStatus, getLatestExploreResult, PortalExploreResult,
  startCrawlerJob, getCrawlerJobStatus, getCrawlerJobs, CrawlerJob,
  startPdfProcessingJob, getPdfProcessingJobStatus, getPdfProcessingJobs, PdfProcessingJob,
  getMcpAgentDownloadUrl, confirmMcpLogin,
  getGroupSettings,
} from '../../services/api';
import { PatientListPanel, EhrSubTabBar, EhrSubTabBtn } from './PatientIntakeTab';

// ── Styles ────────────────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SubTabBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
`;

const SubTab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: 1px solid ${(p) => (p.$active ? '#3b82f6' : 'transparent')};
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: ${(p) => (p.$active ? '#eff6ff' : 'transparent')};
  color: ${(p) => (p.$active ? '#1d4ed8' : '#64748b')};
  cursor: pointer;
  margin-bottom: -1px;
  &:hover { color: #1e40af; background: ${(p) => (p.$active ? '#eff6ff' : '#f8fafc')}; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
`;

const CardTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
`;

const StepLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
`;

const CardDesc = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const LogoImg = styled.img`
  width: 160px;
  height: 44px;
  border-radius: 8px;
  object-fit: contain;
  background: #fff;
  border: 1px solid #e5e7eb;
  padding: 6px 12px;
  flex-shrink: 0;
`;

const SourceInfo = styled.div`
  flex: 1;
  min-width: 160px;
`;

const SourceName = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
`;

const SourceSub = styled.div`
  font-size: 0.8125rem;
  color: #6b7280;
  margin-top: 2px;
`;

const ActionBtn = styled.button<{ $running?: boolean; $variant?: 'primary' | 'green' | 'indigo' }>`
  padding: 10px 20px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  background: ${(p) =>
    p.$running ? '#6b7280' :
    p.$variant === 'green' ? 'linear-gradient(135deg, #059669, #047857)' :
    p.$variant === 'indigo' ? 'linear-gradient(135deg, #6366f1, #4f46e5)' :
    'linear-gradient(135deg, #2563eb, #1d4ed8)'};
  border: none;
  border-radius: 8px;
  cursor: ${(p) => (p.$running ? 'not-allowed' : 'pointer')};
  white-space: nowrap;
  &:hover:not(:disabled) { filter: brightness(1.05); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const CdpNote = styled.div`
  margin-top: 12px;
  font-size: 0.8125rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 14px;
  line-height: 1.5;
`;

const AgentDownloadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const DownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: linear-gradient(135deg, #0f172a, #1e293b);
  color: #f8fafc;
  font-size: 0.8125rem;
  font-weight: 600;
  border-radius: 7px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { filter: brightness(1.15); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const GuidedRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;
  font-size: 0.8125rem;
  color: #374151;
  cursor: pointer;
  user-select: none;
  max-width: 520px;
  input { margin-top: 2px; flex-shrink: 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const ProgressSection = styled.div`
  margin-top: 20px;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8125rem;
  color: #374151;
  margin-bottom: 6px;
`;

const ProgressTrack = styled.div`
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $done: boolean; $failed: boolean; $green?: boolean; $indigo?: boolean }>`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: ${(p) =>
    p.$failed ? '#ef4444' :
    p.$done ? '#10b981' :
    p.$green ? 'linear-gradient(90deg, #10b981, #059669)' :
    p.$indigo ? 'linear-gradient(90deg, #6366f1, #4f46e5)' :
    'linear-gradient(90deg, #3b82f6, #2563eb)'};
  border-radius: 999px;
  transition: width 0.4s ease;
  animation: ${(p) => (!p.$done && !p.$failed && p.$pct > 0 ? pulse : 'none')} 2s infinite;
`;

const StatusMsg = styled.div<{ $failed?: boolean }>`
  margin-top: 8px;
  font-size: 0.8125rem;
  color: ${(p) => (p.$failed ? '#dc2626' : '#6b7280')};
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  color: #6b7280;
  font-weight: 500;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 8px 12px;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) =>
    p.$status === 'completed' ? '#d1fae5' :
    p.$status === 'failed' ? '#fee2e2' :
    p.$status === 'running' ? '#dbeafe' : '#f3f4f6'};
  color: ${(p) =>
    p.$status === 'completed' ? '#065f46' :
    p.$status === 'failed' ? '#991b1b' :
    p.$status === 'running' ? '#1e40af' : '#374151'};
`;

const Placeholder = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #64748b;
  font-size: 0.9375rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #e5e7eb;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const StatCard = styled.div`
  flex: 1;
  min-width: 120px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  padding: 14px 18px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0369a1;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #0284c7;
  margin-top: 2px;
`;

// ── Page selector ─────────────────────────────────────────────────────────────

const PageSelectorWrap = styled.div`
  margin-top: 16px;
`;

const PageSelectorLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const PageModeRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const PageModeBtn = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  font-size: 0.8125rem;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$active ? '#2563eb' : '#d1d5db')};
  background: ${(p) => (p.$active ? '#eff6ff' : '#fff')};
  color: ${(p) => (p.$active ? '#1d4ed8' : '#374151')};
  cursor: pointer;
  &:hover { border-color: #2563eb; color: #1d4ed8; }
`;

const PageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 140px;
  overflow-y: auto;
  padding: 4px 0;
`;

const PageChip = styled.button<{ $selected: boolean }>`
  width: 36px;
  height: 28px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 5px;
  border: 1px solid ${(p) => (p.$selected ? '#2563eb' : '#d1d5db')};
  background: ${(p) => (p.$selected ? '#2563eb' : '#fff')};
  color: ${(p) => (p.$selected ? '#fff' : '#374151')};
  cursor: pointer;
  &:hover { border-color: #2563eb; }
`;

const LoginPrompt = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
`;

const LoginPromptText = styled.p`
  font-size: 0.875rem;
  color: #1e40af;
  margin: 0 0 12px;
  line-height: 1.5;
  a { color: #1d4ed8; }
`;

const LoginPromptActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const CancelBtn = styled.button`
  font-size: 0.8125rem;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  &:hover { color: #374151; }
`;

const ConfirmLoginBanner = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f5f3ff;
  border: 1px solid #c4b5fd;
  border-radius: 10px;
`;

const ConfirmLoginText = styled.p`
  font-size: 0.875rem;
  color: #4c1d95;
  margin: 0 0 12px;
  line-height: 1.5;
`;

const ConfirmLoginBtn = styled.button`
  padding: 10px 22px;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover { filter: brightness(1.08); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// ── Verbose debug console ─────────────────────────────────────────────────────

const VerboseConsole = styled.div`
  margin-top: 12px;
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: 'SF Mono', 'Fira Mono', 'Consolas', monospace;
  font-size: 0.72rem;
  color: #94a3b8;
  max-height: 240px;
  overflow-y: auto;
  line-height: 1.6;
`;

const VerboseLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-top: 12px;
  margin-bottom: 4px;
`;

const VERBOSE = process.env.REACT_APP_VERBOSE === 'true';

// ── ModalityTabShell — wraps any vendor's automation steps with Automation | Patients sub-tabs ──

const ModalityTabShell: React.FC<{ sourceVendor: string; children: React.ReactNode }> = ({ sourceVendor, children }) => {
  const [activeTab, setActiveTab] = useState<'automation' | 'patients'>('automation');
  return (
    <>
      <EhrSubTabBar>
        <EhrSubTabBtn $active={activeTab === 'automation'} onClick={() => setActiveTab('automation')}>
          Automation
        </EhrSubTabBtn>
        <EhrSubTabBtn $active={activeTab === 'patients'} onClick={() => setActiveTab('patients')}>
          Patients
        </EhrSubTabBtn>
      </EhrSubTabBar>
      {activeTab === 'automation' && <>{children}</>}
      {activeTab === 'patients'   && <PatientListPanel sourceVendor={sourceVendor} />}
    </>
  );
};

// ── Vibrant Wellness tab ──────────────────────────────────────────────────────

const POLL_MS = 3000;

const VibrantTab: React.FC = () => {
  // Explore state
  const [exploreJob, setExploreJob] = useState<PortalExploreResult | null>(null);
  const [exploring, setExploring] = useState(false);
  const [exploreError, setExploreError] = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [confirmingLogin, setConfirmingLogin] = useState(false);
  const explorePollRef = useRef<ReturnType<typeof setInterval> | null>(null);


  // Page selector
  const [pageMode, setPageMode] = useState<'all' | 'select'>('all');
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());

  // Crawler state
  const [crawlerJob, setCrawlerJob] = useState<CrawlerJob | null>(null);
  const [crawlerHistory, setCrawlerHistory] = useState<CrawlerJob[]>([]);
  const [crawlerStarting, setCrawlerStarting] = useState(false);
  const [crawlerError, setCrawlerError] = useState<string | null>(null);
  const [guidedMode, setGuidedMode] = useState(true);
  const crawlerPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // PDF processing state
  const [agentDownloading, setAgentDownloading] = useState(false);

  const [processJob, setProcessJob] = useState<PdfProcessingJob | null>(null);
  const [processHistory, setProcessHistory] = useState<PdfProcessingJob[]>([]);
  const [processStarting, setProcessStarting] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const processPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleConfirmLogin = async () => {
    setConfirmingLogin(true);
    try {
      await confirmMcpLogin();
    } catch { /* agent will timeout on its own */ }
    finally { setConfirmingLogin(false); }
  };

  // ── Explore polling ──
  const stopExplorePoll = useCallback(() => {
    if (explorePollRef.current) { clearInterval(explorePollRef.current); explorePollRef.current = null; }
  }, []);

  const pollExplore = useCallback(async (jobId: string) => {
    try {
      const res = await getExploreStatus(jobId);
      setExploreJob(res);
      if (res.status === 'completed' || res.status === 'failed') stopExplorePoll();
    } catch { /* network blip */ }
  }, [stopExplorePoll]);

  const handleExplore = async () => {
    setShowLoginPrompt(false);
    setExploreError(null);
    setExploring(true);
    setExploreJob(null);
    try {
      const res = await explorePortal('vibrant_wellness');
      if (!res.success || !res.job_id) { setExploreError(res.error || 'Failed to start exploration.'); return; }
      const status = await getExploreStatus(res.job_id);
      setExploreJob(status);
      stopExplorePoll();
      explorePollRef.current = setInterval(() => pollExplore(res.job_id!), POLL_MS);
    } catch (e: any) {
      setExploreError(e?.message || 'Failed to connect to portal.');
    } finally {
      setExploring(false);
    }
  };

  // ── Crawler polling ──
  const stopCrawlerPoll = useCallback(() => {
    if (crawlerPollRef.current) { clearInterval(crawlerPollRef.current); crawlerPollRef.current = null; }
  }, []);

  const pollCrawler = useCallback(async (jobId: string) => {
    try {
      const res = await getCrawlerJobStatus(jobId);
      setCrawlerJob(res);
      if (res.status === 'completed' || res.status === 'failed') {
        stopCrawlerPoll();
        getCrawlerJobs('vibrant_wellness').then((r) => setCrawlerHistory(r.jobs)).catch(() => {});
      }
    } catch { /* network blip */ }
  }, [stopCrawlerPoll]);

  const startCrawlerPoll = useCallback((jobId: string) => {
    stopCrawlerPoll();
    crawlerPollRef.current = setInterval(() => pollCrawler(jobId), POLL_MS);
  }, [pollCrawler, stopCrawlerPoll]);

  // ── PDF processing polling ──
  const stopProcessPoll = useCallback(() => {
    if (processPollRef.current) { clearInterval(processPollRef.current); processPollRef.current = null; }
  }, []);

  const pollProcess = useCallback(async (jobId: string) => {
    try {
      const res = await getPdfProcessingJobStatus(jobId);
      setProcessJob(res);
      if (res.status === 'completed' || res.status === 'failed') {
        stopProcessPoll();
        getPdfProcessingJobs().then((r) => setProcessHistory(r.jobs)).catch(() => {});
      }
    } catch { /* network blip */ }
  }, [stopProcessPoll]);

  const startProcessPoll = useCallback((jobId: string) => {
    stopProcessPoll();
    processPollRef.current = setInterval(() => pollProcess(jobId), POLL_MS);
  }, [pollProcess, stopProcessPoll]);

  useEffect(() => {
    getLatestExploreResult('vibrant_wellness').then((res) => {
      if (res.success) setExploreJob(res);
    }).catch(() => {});

    getCrawlerJobs('vibrant_wellness').then((res) => {
      setCrawlerHistory(res.jobs);
      const running = res.jobs.find((j) => j.status === 'running' || j.status === 'pending');
      if (running) { setCrawlerJob(running); startCrawlerPoll(running.job_id); }
    }).catch(() => {});

    getPdfProcessingJobs().then((res) => {
      setProcessHistory(res.jobs);
      const running = res.jobs.find((j) => j.status === 'running' || j.status === 'pending');
      if (running) { setProcessJob(running); startProcessPoll(running.job_id); }
    }).catch(() => {});

    return () => { stopCrawlerPoll(); stopProcessPoll(); stopExplorePoll(); };
  }, [startCrawlerPoll, stopCrawlerPoll, startProcessPoll, stopProcessPoll, stopExplorePoll]);

  const handleStartCrawler = async () => {
    setCrawlerError(null);
    setCrawlerStarting(true);
    const pages = pageMode === 'select' && selectedPages.size > 0
      ? Array.from(selectedPages).sort((a, b) => a - b)
      : 'all';
    try {
      const res = await startCrawlerJob('vibrant_wellness', { guided_mode: guidedMode, pages });
      if (!res.success || !res.job_id) { setCrawlerError(res.error || 'Failed to start.'); return; }
      const status = await getCrawlerJobStatus(res.job_id);
      setCrawlerJob(status);
      startCrawlerPoll(res.job_id);
    } catch (e: any) {
      setCrawlerError(e?.message || 'Failed to start integration.');
    } finally {
      setCrawlerStarting(false);
    }
  };

  const handleStartProcess = async () => {
    setProcessError(null);
    setProcessStarting(true);
    try {
      const res = await startPdfProcessingJob('vibrant_wellness');
      if (!res.success || !res.job_id) { setProcessError(res.error || 'Failed to start.'); return; }
      const s = await getPdfProcessingJobStatus(res.job_id);
      setProcessJob(s);
      startProcessPoll(res.job_id);
    } catch (e: any) {
      setProcessError(e?.message || 'Failed to start PDF processing.');
    } finally {
      setProcessStarting(false);
    }
  };

  const togglePage = (n: number) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  };

  const crawlerRunning = crawlerJob?.status === 'running' || crawlerJob?.status === 'pending';
  const processRunning = processJob?.status === 'running' || processJob?.status === 'pending';
  const exploreRunning = exploreJob?.status === 'running' || exploreJob?.status === 'pending' || exploring;
  const totalPages = exploreJob?.status === 'completed' ? exploreJob.total_pages : 0;
  const canDownload = !crawlerRunning && !crawlerStarting && (pageMode === 'all' || selectedPages.size > 0);

  const automationContent = (
    <Container>
      {/* Step 1 — Explore */}
      <Card>
        <StepLabel>Step 1 — Explore Portal</StepLabel>
        <CardTitle>Preview</CardTitle>
        <CardDesc>
          Connect to the Vibrant portal in read-only mode to see how many pages and patients are available
          before starting a download. Takes ~30 seconds.
        </CardDesc>

        <SourceRow>
          <LogoImg src={vibrantLogo} alt="Vibrant Wellness" />
          <SourceInfo>
            <SourceName>Vibrant Wellness</SourceName>
            <SourceSub>Read-only scan · no downloads</SourceSub>
          </SourceInfo>
          <ActionBtn
            $variant="indigo"
            $running={exploreRunning}
            disabled={exploreRunning}
            onClick={() => setShowLoginPrompt(true)}
          >
            {exploreRunning ? 'Exploring...' : 'Explore Portal'}
          </ActionBtn>
        </SourceRow>

        <CdpNote>
          <strong>Requires the DrSchmoe Agent desktop app.</strong>
          <br />
          The Agent runs on your Mac and connects Chrome to this portal automatically.
          <AgentDownloadRow>
            <DownloadBtn
              disabled={agentDownloading}
              onClick={() => {
                setAgentDownloading(true);
                getMcpAgentDownloadUrl()
                  .then((res) => { window.location.href = res.url; })
                  .catch(() => {})
                  .finally(() => setAgentDownloading(false));
              }}
            >
              {agentDownloading ? 'Preparing…' : '↓ Download DrSchmoe Agent (macOS)'}
            </DownloadBtn>
            <span style={{ fontSize: '0.75rem', color: '#92400e' }}>
              Install, open, and paste your pairing token from Settings → Local Agent.
            </span>
          </AgentDownloadRow>
        </CdpNote>

        {showLoginPrompt && !exploreRunning && (
          <LoginPrompt>
            <LoginPromptText>
              DrSchmoe Agent will open Chrome automatically.{' '}
              Log into <strong>portal.vibrant-wellness.com</strong> when Chrome opens, then click{' '}
              <strong>"I'm Logged In"</strong> to start the scan.
            </LoginPromptText>
            <LoginPromptActions>
              <ActionBtn $variant="indigo" $running={false} onClick={handleExplore}>
                Open Chrome &amp; Start Exploring
              </ActionBtn>
              <CancelBtn onClick={() => setShowLoginPrompt(false)}>Cancel</CancelBtn>
            </LoginPromptActions>
          </LoginPrompt>
        )}

        {exploreError && <StatusMsg $failed>{exploreError}</StatusMsg>}

        {exploreJob?.status === 'running' && !exploreJob.message?.toLowerCase().includes('log into') && (
          <StatusMsg>{exploreJob.message || 'Scanning portal...'}</StatusMsg>
        )}

        {exploreJob?.status === 'running' && exploreJob.message?.toLowerCase().includes('log into') && (
          <ConfirmLoginBanner>
            <ConfirmLoginText>
              Chrome is open — <strong>log into portal.vibrant-wellness.com</strong>, then click the button below to start the scan.
            </ConfirmLoginText>
            <ConfirmLoginBtn onClick={handleConfirmLogin} disabled={confirmingLogin}>
              {confirmingLogin ? 'Confirming...' : "I'm Logged In — Start Exploring"}
            </ConfirmLoginBtn>
          </ConfirmLoginBanner>
        )}

        {exploreJob?.status === 'completed' && (
          <StatsRow>
            <StatCard>
              <StatValue>{exploreJob.total_pages}</StatValue>
              <StatLabel>Pages</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>~{exploreJob.patients_per_page}</StatValue>
              <StatLabel>Patients / page</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>~{exploreJob.estimated_patients}</StatValue>
              <StatLabel>Total patients</StatLabel>
            </StatCard>
          </StatsRow>
        )}

        {exploreJob?.status === 'failed' && (
          <StatusMsg $failed>{exploreJob.error}</StatusMsg>
        )}
      </Card>

      {/* Step 2 — Download */}
      <Card>
        <StepLabel>Step 2 — Download Reports</StepLabel>
        <CardTitle>Data Integration</CardTitle>
        <CardDesc>
          Select which pages to download and start the automated ZIP download.
          With <strong>Guided mode</strong> on, a banner and green highlights show what the crawler is doing.
        </CardDesc>

        <PageSelectorWrap>
          <PageSelectorLabel>Pages to download</PageSelectorLabel>
          {totalPages === 0 ? (
            <CardDesc style={{ color: '#6b7280', fontStyle: 'italic' }}>
              Run Explore first to detect the number of available pages.
            </CardDesc>
          ) : (
          <PageModeRow>
            <PageModeBtn $active={pageMode === 'all'} onClick={() => setPageMode('all')}>
              All {totalPages} pages
            </PageModeBtn>
            <PageModeBtn $active={pageMode === 'select'} onClick={() => setPageMode('select')}>
              Select specific pages
            </PageModeBtn>
          </PageModeRow>
          )}

          {totalPages > 0 && pageMode === 'select' && (
            <>
              <PageGrid>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <PageChip key={n} $selected={selectedPages.has(n)} onClick={() => togglePage(n)}>
                    {n}
                  </PageChip>
                ))}
              </PageGrid>
              <StatusMsg style={{ marginTop: 8 }}>
                {selectedPages.size === 0
                  ? 'Select at least one page.'
                  : `${selectedPages.size} page${selectedPages.size !== 1 ? 's' : ''} selected · ~${selectedPages.size * (exploreJob?.patients_per_page || 10)} patients`}
              </StatusMsg>
            </>
          )}
        </PageSelectorWrap>

        <GuidedRow>
          <input type="checkbox" checked={guidedMode} onChange={(e) => setGuidedMode(e.target.checked)} />
          <span>
            <strong>Guided mode</strong> — show a status banner on the Vibrant tab, highlight each button/row,
            and use slower pacing (recommended). Turn off for a faster, minimal crawl.
          </span>
        </GuidedRow>

        <div style={{ marginTop: 16 }}>
          <ActionBtn
            $running={crawlerRunning}
            disabled={!canDownload}
            onClick={handleStartCrawler}
          >
            {crawlerStarting ? 'Starting...' : crawlerRunning ? 'Download Running...' : 'Start Download'}
          </ActionBtn>
        </div>

        {crawlerError && <StatusMsg $failed>{crawlerError}</StatusMsg>}

        {crawlerJob && (
          <ProgressSection>
            <ProgressLabel>
              <span>
                {crawlerJob.status === 'completed' ? '✓ Complete' :
                 crawlerJob.status === 'failed' ? '✗ Failed' :
                 `Page ${crawlerJob.current_page} of ${crawlerJob.total_pages}`}
                {crawlerJob.downloaded_files > 0 && ` · ${crawlerJob.downloaded_files} ZIP files`}
              </span>
              <span>{crawlerJob.percent.toFixed(1)}%</span>
            </ProgressLabel>
            <ProgressTrack>
              <ProgressFill
                $pct={crawlerJob.percent}
                $done={crawlerJob.status === 'completed'}
                $failed={crawlerJob.status === 'failed'}
              />
            </ProgressTrack>
            <StatusMsg $failed={crawlerJob.status === 'failed'}>
              {crawlerJob.status === 'failed' ? crawlerJob.error : crawlerJob.message}
            </StatusMsg>
            {VERBOSE && crawlerJob.detail_log && crawlerJob.detail_log.length > 0 && (
              <>
                <VerboseLabel>Agent Debug Log ({crawlerJob.detail_log.length} lines)</VerboseLabel>
                <VerboseConsole ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
                  {crawlerJob.detail_log.map((line, i) => (
                    <div key={i} style={{ color: line.includes('failed') || line.includes('error') || line.includes('Error') ? '#f87171' : line.includes('Extracted') || line.includes('ZIP detected') || line.includes('renamed') ? '#4ade80' : '#94a3b8' }}>
                      {line}
                    </div>
                  ))}
                </VerboseConsole>
              </>
            )}
          </ProgressSection>
        )}

        {crawlerHistory.length > 0 && (
          <ProgressSection>
            <HistoryTable>
              <thead>
                <tr>
                  <Th>Status</Th><Th>Progress</Th><Th>ZIPs</Th><Th>Pages</Th><Th>Guided</Th><Th>Started</Th>
                </tr>
              </thead>
              <tbody>
                {crawlerHistory.map((job) => (
                  <tr key={job.job_id}>
                    <Td><StatusBadge $status={job.status}>{job.status}</StatusBadge></Td>
                    <Td>{job.percent.toFixed(1)}% ({job.current_page}/{job.total_pages})</Td>
                    <Td>{job.downloaded_files}</Td>
                    <Td>{job.selected_pages ? `${job.selected_pages.length} selected` : 'All'}</Td>
                    <Td>{job.guided_mode === false ? 'Off' : 'On'}</Td>
                    <Td>{new Date(job.created_at).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          </ProgressSection>
        )}
      </Card>

      {/* Step 3 — Process PDFs (hidden until ready for production release) */}
      {false && <Card>
        <StepLabel>Step 3 — Extract &amp; Generate JSON</StepLabel>
        <CardTitle>Process PDFs</CardTitle>
        <CardDesc>
          Run AI vision extraction on all downloaded Vibrant PDFs. Each report is sent to Claude
          (or GPT-4o as fallback) to extract structured patient demographics and lab results.
          Results are saved to a single <code>gpt_results.json</code> file, cached incrementally —
          already-processed PDFs are skipped on re-runs.
        </CardDesc>

        <SourceRow>
          <LogoImg src={vibrantLogo} alt="Vibrant Wellness" />
          <SourceInfo>
            <SourceName>Vibrant Wellness PDFs</SourceName>
            <SourceSub>Claude vision extraction · incremental</SourceSub>
          </SourceInfo>
          <ActionBtn
            $variant="green"
            $running={processRunning}
            disabled={processRunning || processStarting}
            onClick={handleStartProcess}
          >
            {processStarting ? 'Starting...' : processRunning ? 'Processing PDFs...' : 'Process PDFs → JSON'}
          </ActionBtn>
        </SourceRow>

        {processError && <StatusMsg $failed>{processError}</StatusMsg>}

        {processJob && (
          <ProgressSection>
            <ProgressLabel>
              <span>
                {processJob.status === 'completed' ? '✓ Complete' :
                 processJob.status === 'failed' ? '✗ Failed' :
                 `${processJob.processed_pdfs} of ${processJob.total_pdfs} PDFs`}
                {processJob.failed_pdfs > 0 && ` · ${processJob.failed_pdfs} failed`}
              </span>
              <span>{processJob.percent.toFixed(1)}%</span>
            </ProgressLabel>
            <ProgressTrack>
              <ProgressFill
                $pct={processJob.percent}
                $done={processJob.status === 'completed'}
                $failed={processJob.status === 'failed'}
                $green
              />
            </ProgressTrack>
            <StatusMsg $failed={processJob.status === 'failed'}>
              {processJob.status === 'failed' ? processJob.error : processJob.message}
            </StatusMsg>
            {processJob.status === 'completed' && processJob.output_path && (
              <StatusMsg style={{ color: '#065f46', marginTop: 6 }}>
                Output: <code style={{ fontSize: '0.75rem' }}>{processJob.output_path}</code>
              </StatusMsg>
            )}
          </ProgressSection>
        )}

        {processHistory.length > 0 && (
          <ProgressSection>
            <HistoryTable>
              <thead>
                <tr>
                  <Th>Status</Th><Th>Progress</Th><Th>Failed</Th><Th>Started</Th>
                </tr>
              </thead>
              <tbody>
                {processHistory.map((job) => (
                  <tr key={job.job_id}>
                    <Td><StatusBadge $status={job.status}>{job.status}</StatusBadge></Td>
                    <Td>{job.percent.toFixed(1)}% ({job.processed_pdfs}/{job.total_pdfs} PDFs)</Td>
                    <Td>{job.failed_pdfs}</Td>
                    <Td>{new Date(job.created_at).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          </ProgressSection>
        )}
      </Card>}
    </Container>
  );

  return <ModalityTabShell sourceVendor="vibrant_wellness">{automationContent}</ModalityTabShell>;
};

// ── Rupa Health tab ───────────────────────────────────────────────────────────

const RupaTab: React.FC = () => {
  const [exploreJob, setExploreJob] = useState<PortalExploreResult | null>(null);
  const [exploring, setExploring]   = useState(false);
  const [exploreError, setExploreError] = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [confirmingLogin, setConfirmingLogin] = useState(false);
  const explorePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [pageMode, setPageMode]       = useState<'all' | 'select'>('all');
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());

  const [crawlerJob, setCrawlerJob]       = useState<CrawlerJob | null>(null);
  const [crawlerHistory, setCrawlerHistory] = useState<CrawlerJob[]>([]);
  const [crawlerStarting, setCrawlerStarting] = useState(false);
  const [crawlerError, setCrawlerError]   = useState<string | null>(null);
  const [guidedMode, setGuidedMode]       = useState(true);
  const [agentDownloading, setAgentDownloading] = useState(false);
  const crawlerPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopExplorePoll = useCallback(() => {
    if (explorePollRef.current) { clearInterval(explorePollRef.current); explorePollRef.current = null; }
  }, []);

  const pollExplore = useCallback(async (jobId: string) => {
    try {
      const res = await getExploreStatus(jobId);
      setExploreJob(res);
      if (res.status === 'completed' || res.status === 'failed') stopExplorePoll();
    } catch { /* blip */ }
  }, [stopExplorePoll]);

  const handleExplore = async () => {
    setShowLoginPrompt(false);
    setExploreError(null);
    setExploring(true);
    setExploreJob(null);
    try {
      const res = await explorePortal('rupa_health');
      if (!res.success || !res.job_id) { setExploreError(res.error || 'Failed to start.'); return; }
      const status = await getExploreStatus(res.job_id);
      setExploreJob(status);
      stopExplorePoll();
      explorePollRef.current = setInterval(() => pollExplore(res.job_id!), POLL_MS);
    } catch (e: any) {
      setExploreError(e?.message || 'Failed to connect.');
    } finally {
      setExploring(false);
    }
  };

  const stopCrawlerPoll = useCallback(() => {
    if (crawlerPollRef.current) { clearInterval(crawlerPollRef.current); crawlerPollRef.current = null; }
  }, []);

  const pollCrawler = useCallback(async (jobId: string) => {
    try {
      const res = await getCrawlerJobStatus(jobId);
      setCrawlerJob(res);
      if (res.status === 'completed' || res.status === 'failed') {
        stopCrawlerPoll();
        getCrawlerJobs('rupa_health').then((r) => setCrawlerHistory(r.jobs)).catch(() => {});
      }
    } catch { /* blip */ }
  }, [stopCrawlerPoll]);

  const startCrawlerPoll = useCallback((jobId: string) => {
    stopCrawlerPoll();
    crawlerPollRef.current = setInterval(() => pollCrawler(jobId), POLL_MS);
  }, [pollCrawler, stopCrawlerPoll]);

  useEffect(() => {
    getLatestExploreResult('rupa_health').then((res) => {
      if (res.success) setExploreJob(res);
    }).catch(() => {});

    getCrawlerJobs('rupa_health').then((res) => {
      setCrawlerHistory(res.jobs);
      const running = res.jobs.find((j) => j.status === 'running' || j.status === 'pending');
      if (running) { setCrawlerJob(running); startCrawlerPoll(running.job_id); }
    }).catch(() => {});
    return () => { stopCrawlerPoll(); stopExplorePoll(); };
  }, [startCrawlerPoll, stopCrawlerPoll, stopExplorePoll]);

  const handleStartCrawler = async () => {
    setCrawlerError(null);
    setCrawlerStarting(true);
    const pages = pageMode === 'select' && selectedPages.size > 0
      ? Array.from(selectedPages).sort((a, b) => a - b)
      : 'all';
    try {
      const res = await startCrawlerJob('rupa_health', { guided_mode: guidedMode, pages });
      if (!res.success || !res.job_id) { setCrawlerError(res.error || 'Failed to start.'); return; }
      const status = await getCrawlerJobStatus(res.job_id);
      setCrawlerJob(status);
      startCrawlerPoll(res.job_id);
    } catch (e: any) {
      setCrawlerError(e?.message || 'Failed to start.');
    } finally {
      setCrawlerStarting(false);
    }
  };

  const togglePage = (n: number) => {
    setSelectedPages((prev) => { const next = new Set(prev); if (next.has(n)) next.delete(n); else next.add(n); return next; });
  };

  const exploreRunning = exploreJob?.status === 'running' || exploreJob?.status === 'pending' || exploring;
  const crawlerRunning = crawlerJob?.status === 'running' || crawlerJob?.status === 'pending';
  const totalPages     = exploreJob?.status === 'completed' ? exploreJob.total_pages : 0;
  const totalPatients  = exploreJob?.status === 'completed' ? exploreJob.estimated_patients : 0;
  const canDownload    = !crawlerRunning && !crawlerStarting && totalPages > 0 && (pageMode === 'all' || selectedPages.size > 0);

  const automationContent = (
    <Container>
      {/* Step 1 — Explore */}
      <Card>
        <StepLabel>Step 1 — Explore Portal</StepLabel>
        <CardTitle>Preview</CardTitle>
        <CardDesc>
          Reads the Rupa Health results table to count total patients and pages — no downloads.
          Takes ~15 seconds.
        </CardDesc>

        <SourceRow>
          <LogoImg src={rupaLogo} alt="Rupa Health" />
          <SourceInfo>
            <SourceName>Rupa Health</SourceName>
            <SourceSub>Read-only scan · no downloads</SourceSub>
          </SourceInfo>
          <ActionBtn
            $variant="indigo"
            $running={exploreRunning}
            disabled={exploreRunning}
            onClick={() => setShowLoginPrompt(true)}
          >
            {exploreRunning ? 'Exploring...' : 'Explore Portal'}
          </ActionBtn>
        </SourceRow>

        <CdpNote>
          <strong>Requires the DrSchmoe Agent desktop app.</strong>
          <br />
          The Agent connects Chrome to the Rupa portal automatically.
          <AgentDownloadRow>
            <DownloadBtn
              disabled={agentDownloading}
              onClick={() => {
                setAgentDownloading(true);
                getMcpAgentDownloadUrl()
                  .then((res) => { window.location.href = res.url; })
                  .catch(() => {})
                  .finally(() => setAgentDownloading(false));
              }}
            >
              {agentDownloading ? 'Preparing…' : '↓ Download DrSchmoe Agent (macOS)'}
            </DownloadBtn>
          </AgentDownloadRow>
        </CdpNote>

        {showLoginPrompt && !exploreRunning && (
          <LoginPrompt>
            <LoginPromptText>
              DrSchmoe Agent will open Chrome automatically.{' '}
              Log into <strong>labs.rupahealth.com</strong> when Chrome opens, then click{' '}
              <strong>"I'm Logged In"</strong> to start the scan.
            </LoginPromptText>
            <LoginPromptActions>
              <ActionBtn $variant="indigo" $running={false} onClick={handleExplore}>
                Open Chrome &amp; Start Exploring
              </ActionBtn>
              <CancelBtn onClick={() => setShowLoginPrompt(false)}>Cancel</CancelBtn>
            </LoginPromptActions>
          </LoginPrompt>
        )}

        {exploreError && <StatusMsg $failed>{exploreError}</StatusMsg>}

        {exploreJob?.status === 'running' && exploreJob.message?.toLowerCase().includes('log into') && (
          <ConfirmLoginBanner>
            <ConfirmLoginText>
              Chrome is open — <strong>log into labs.rupahealth.com</strong>, then click below.
            </ConfirmLoginText>
            <ConfirmLoginBtn onClick={async () => { setConfirmingLogin(true); try { await confirmMcpLogin(); } catch {} finally { setConfirmingLogin(false); } }} disabled={confirmingLogin}>
              {confirmingLogin ? 'Confirming...' : "I'm Logged In — Start Exploring"}
            </ConfirmLoginBtn>
          </ConfirmLoginBanner>
        )}

        {exploreJob?.status === 'running' && !exploreJob.message?.toLowerCase().includes('log into') && (
          <StatusMsg>{exploreJob.message || 'Scanning portal...'}</StatusMsg>
        )}

        {exploreJob?.status === 'completed' && (
          <StatsRow>
            <StatCard>
              <StatValue>{totalPatients}</StatValue>
              <StatLabel>Total patients</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{exploreJob.patients_per_page}</StatValue>
              <StatLabel>Per page</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{totalPages}</StatValue>
              <StatLabel>Pages</StatLabel>
            </StatCard>
          </StatsRow>
        )}

        {exploreJob?.status === 'failed' && <StatusMsg $failed>{exploreJob.error}</StatusMsg>}
      </Card>

      {/* Step 2 — Download */}
      <Card>
        <StepLabel>Step 2 — Download PDFs</StepLabel>
        <CardTitle>Data Integration</CardTitle>
        <CardDesc>
          Downloads each test PDF for every patient. With <strong>Guided mode</strong> on,
          a banner and green highlights show what the crawler is doing.
          {totalPages === 0 && <span style={{ color: '#92400e' }}> Run Explore first to detect page count.</span>}
        </CardDesc>

        {totalPages > 0 && (
          <PageSelectorWrap>
            <PageSelectorLabel>Pages to download</PageSelectorLabel>
            <PageModeRow>
              <PageModeBtn $active={pageMode === 'all'} onClick={() => setPageMode('all')}>
                All {totalPages} pages
              </PageModeBtn>
              <PageModeBtn $active={pageMode === 'select'} onClick={() => setPageMode('select')}>
                Select specific pages
              </PageModeBtn>
            </PageModeRow>
            {pageMode === 'select' && (
              <>
                <PageGrid>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <PageChip key={n} $selected={selectedPages.has(n)} onClick={() => togglePage(n)}>{n}</PageChip>
                  ))}
                </PageGrid>
                <StatusMsg style={{ marginTop: 8 }}>
                  {selectedPages.size === 0 ? 'Select at least one page.' : `${selectedPages.size} page${selectedPages.size !== 1 ? 's' : ''} selected`}
                </StatusMsg>
              </>
            )}
          </PageSelectorWrap>
        )}

        <GuidedRow>
          <input type="checkbox" checked={guidedMode} onChange={(e) => setGuidedMode(e.target.checked)} />
          <span>
            <strong>Guided mode</strong> — show a status banner on the Rupa tab, highlight each button,
            and use slower pacing. Turn off for a faster, minimal crawl.
          </span>
        </GuidedRow>

        <div style={{ marginTop: 16 }}>
          <ActionBtn $running={crawlerRunning} disabled={!canDownload} onClick={handleStartCrawler}>
            {crawlerStarting ? 'Starting...' : crawlerRunning ? 'Download Running...' : 'Start Download'}
          </ActionBtn>
        </div>

        {crawlerError && <StatusMsg $failed>{crawlerError}</StatusMsg>}

        {crawlerJob && (
          <ProgressSection>
            <ProgressLabel>
              <span>
                {crawlerJob.status === 'completed' ? '✓ Complete' :
                 crawlerJob.status === 'failed'    ? '✗ Failed' :
                 `Page ${crawlerJob.current_page} of ${crawlerJob.total_pages}`}
                {crawlerJob.downloaded_files > 0 && ` · ${crawlerJob.downloaded_files} PDFs`}
              </span>
              <span>{crawlerJob.percent.toFixed(1)}%</span>
            </ProgressLabel>
            <ProgressTrack>
              <ProgressFill $pct={crawlerJob.percent} $done={crawlerJob.status === 'completed'} $failed={crawlerJob.status === 'failed'} />
            </ProgressTrack>
            <StatusMsg $failed={crawlerJob.status === 'failed'}>
              {crawlerJob.status === 'failed' ? crawlerJob.error : crawlerJob.message}
            </StatusMsg>
          </ProgressSection>
        )}

        {crawlerHistory.length > 0 && (
          <ProgressSection>
            <HistoryTable>
              <thead><tr><Th>Status</Th><Th>Progress</Th><Th>PDFs</Th><Th>Guided</Th><Th>Started</Th></tr></thead>
              <tbody>
                {crawlerHistory.map((job) => (
                  <tr key={job.job_id}>
                    <Td><StatusBadge $status={job.status}>{job.status}</StatusBadge></Td>
                    <Td>{job.percent.toFixed(1)}% ({job.current_page}/{job.total_pages})</Td>
                    <Td>{job.downloaded_files}</Td>
                    <Td>{job.guided_mode === false ? 'Off' : 'On'}</Td>
                    <Td>{new Date(job.created_at).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          </ProgressSection>
        )}
      </Card>
    </Container>
  );

  return <ModalityTabShell sourceVendor="rupa_health">{automationContent}</ModalityTabShell>;
};

// ── MicrobiologyDx tab ────────────────────────────────────────────────────────

export const MicrobiologyDxTab: React.FC = () => {
  const [exploreJob, setExploreJob]         = useState<PortalExploreResult | null>(null);
  const [exploring, setExploring]           = useState(false);
  const [exploreError, setExploreError]     = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [confirmingLogin, setConfirmingLogin] = useState(false);
  const explorePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [pageMode, setPageMode]             = useState<'all' | 'select'>('all');
  const [selectedPages, setSelectedPages]   = useState<Set<number>>(new Set());
  const [crawlerJob, setCrawlerJob]         = useState<CrawlerJob | null>(null);
  const [crawlerHistory, setCrawlerHistory] = useState<CrawlerJob[]>([]);
  const [crawlerStarting, setCrawlerStarting] = useState(false);
  const [crawlerError, setCrawlerError]     = useState<string | null>(null);
  const [guidedMode, setGuidedMode]         = useState(true);
  const [agentDownloading, setAgentDownloading] = useState(false);
  const crawlerPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopExplorePoll = useCallback(() => {
    if (explorePollRef.current) { clearInterval(explorePollRef.current); explorePollRef.current = null; }
  }, []);

  const pollExplore = useCallback(async (jobId: string) => {
    try {
      const res = await getExploreStatus(jobId);
      setExploreJob(res);
      if (res.status === 'completed' || res.status === 'failed') stopExplorePoll();
    } catch { /* blip */ }
  }, [stopExplorePoll]);

  const handleExplore = async () => {
    setShowLoginPrompt(false);
    setExploreError(null);
    setExploring(true);
    setExploreJob(null);
    try {
      const res = await explorePortal('microbiologydx');
      if (!res.success || !res.job_id) { setExploreError(res.error || 'Failed to start.'); return; }
      const status = await getExploreStatus(res.job_id);
      setExploreJob(status);
      stopExplorePoll();
      explorePollRef.current = setInterval(() => pollExplore(res.job_id!), POLL_MS);
    } catch (e: any) {
      setExploreError(e?.message || 'Failed to connect.');
    } finally {
      setExploring(false);
    }
  };

  const stopCrawlerPoll = useCallback(() => {
    if (crawlerPollRef.current) { clearInterval(crawlerPollRef.current); crawlerPollRef.current = null; }
  }, []);

  const pollCrawler = useCallback(async (jobId: string) => {
    try {
      const res = await getCrawlerJobStatus(jobId);
      setCrawlerJob(res);
      if (res.status === 'completed' || res.status === 'failed') {
        stopCrawlerPoll();
        getCrawlerJobs('microbiologydx').then((r) => setCrawlerHistory(r.jobs)).catch(() => {});
      }
    } catch { /* blip */ }
  }, [stopCrawlerPoll]);

  const startCrawlerPoll = useCallback((jobId: string) => {
    stopCrawlerPoll();
    crawlerPollRef.current = setInterval(() => pollCrawler(jobId), POLL_MS);
  }, [pollCrawler, stopCrawlerPoll]);

  useEffect(() => {
    getLatestExploreResult('microbiologydx').then((res) => {
      if (res.success) setExploreJob(res);
    }).catch(() => {});
    getCrawlerJobs('microbiologydx').then((res) => {
      setCrawlerHistory(res.jobs);
      const running = res.jobs.find((j) => j.status === 'running' || j.status === 'pending');
      if (running) { setCrawlerJob(running); startCrawlerPoll(running.job_id); }
    }).catch(() => {});
    return () => { stopCrawlerPoll(); stopExplorePoll(); };
  }, [startCrawlerPoll, stopCrawlerPoll, stopExplorePoll]);

  const handleStartCrawler = async () => {
    setCrawlerError(null);
    setCrawlerStarting(true);
    const pages = pageMode === 'select' && selectedPages.size > 0
      ? Array.from(selectedPages).sort((a, b) => a - b)
      : 'all';
    try {
      const res = await startCrawlerJob('microbiologydx', { guided_mode: guidedMode, pages });
      if (!res.success || !res.job_id) { setCrawlerError(res.error || 'Failed to start.'); return; }
      const status = await getCrawlerJobStatus(res.job_id);
      setCrawlerJob(status);
      startCrawlerPoll(res.job_id);
    } catch (e: any) {
      setCrawlerError(e?.message || 'Failed to start.');
    } finally {
      setCrawlerStarting(false);
    }
  };

  const togglePage = (n: number) => {
    setSelectedPages((prev) => { const next = new Set(prev); if (next.has(n)) next.delete(n); else next.add(n); return next; });
  };

  const exploreRunning = exploreJob?.status === 'running' || exploreJob?.status === 'pending' || exploring;
  const crawlerRunning = crawlerJob?.status === 'running'  || crawlerJob?.status === 'pending';
  const totalPages     = exploreJob?.status === 'completed' ? exploreJob.total_pages : 0;
  const totalPatients  = exploreJob?.status === 'completed' ? exploreJob.estimated_patients : 0;
  const canDownload    = !crawlerRunning && !crawlerStarting && totalPages > 0 && (pageMode === 'all' || selectedPages.size > 0);

  const automationContent = (
    <Container>
      {/* Step 1 — Explore */}
      <Card>
        <StepLabel>Step 1 — Explore Portal</StepLabel>
        <CardTitle>Preview</CardTitle>
        <CardDesc>
          Reads the MicrobiologyDx patient results list to count total patients and pages — no downloads.
        </CardDesc>

        <SourceRow>
          <LogoImg src={microbiologydxLogo} alt="MicrobiologyDx" />
          <SourceInfo>
            <SourceName>MicrobiologyDx (MARCoNS)</SourceName>
            <SourceSub>labportal.cislab.com/mdx/ · Read-only scan</SourceSub>
          </SourceInfo>
          <ActionBtn
            $variant="indigo"
            $running={exploreRunning}
            disabled={exploreRunning}
            onClick={() => setShowLoginPrompt(true)}
          >
            {exploreRunning ? 'Exploring...' : 'Explore Portal'}
          </ActionBtn>
        </SourceRow>

        <CdpNote>
          <strong>Requires the DrSchmoe Agent desktop app.</strong>
          <br />
          The Agent connects Chrome to the MicrobiologyDx portal automatically.
          <AgentDownloadRow>
            <DownloadBtn
              disabled={agentDownloading}
              onClick={() => {
                setAgentDownloading(true);
                getMcpAgentDownloadUrl()
                  .then((res) => { window.location.href = res.url; })
                  .catch(() => {})
                  .finally(() => setAgentDownloading(false));
              }}
            >
              {agentDownloading ? 'Preparing…' : '↓ Download DrSchmoe Agent (macOS)'}
            </DownloadBtn>
          </AgentDownloadRow>
        </CdpNote>

        {showLoginPrompt && !exploreRunning && (
          <LoginPrompt>
            <LoginPromptText>
              DrSchmoe Agent will open Chrome automatically.{' '}
              Log into <strong>labportal.cislab.com/mdx/</strong> when Chrome opens, then click{' '}
              <strong>"I'm Logged In"</strong> to start the scan.
            </LoginPromptText>
            <LoginPromptActions>
              <ActionBtn $variant="indigo" $running={false} onClick={handleExplore}>
                Open Chrome &amp; Start Exploring
              </ActionBtn>
              <CancelBtn onClick={() => setShowLoginPrompt(false)}>Cancel</CancelBtn>
            </LoginPromptActions>
          </LoginPrompt>
        )}

        {exploreError && <StatusMsg $failed>{exploreError}</StatusMsg>}

        {exploreJob?.status === 'running' && exploreJob.message?.toLowerCase().includes('log into') && (
          <ConfirmLoginBanner>
            <ConfirmLoginText>
              Chrome is open — <strong>log into labportal.cislab.com/mdx/</strong>, then click below.
            </ConfirmLoginText>
            <ConfirmLoginBtn
              onClick={async () => { setConfirmingLogin(true); try { await confirmMcpLogin(); } catch {} finally { setConfirmingLogin(false); } }}
              disabled={confirmingLogin}
            >
              {confirmingLogin ? 'Confirming...' : "I'm Logged In — Start Exploring"}
            </ConfirmLoginBtn>
          </ConfirmLoginBanner>
        )}

        {exploreJob?.status === 'running' && !exploreJob.message?.toLowerCase().includes('log into') && (
          <StatusMsg>{exploreJob.message || 'Scanning portal...'}</StatusMsg>
        )}

        {exploreJob?.status === 'completed' && (
          <StatsRow>
            <StatCard><StatValue>{totalPatients}</StatValue><StatLabel>Total patients</StatLabel></StatCard>
            <StatCard><StatValue>{exploreJob.patients_per_page}</StatValue><StatLabel>Per page</StatLabel></StatCard>
            <StatCard><StatValue>{totalPages}</StatValue><StatLabel>Pages</StatLabel></StatCard>
          </StatsRow>
        )}

        {exploreJob?.status === 'failed' && <StatusMsg $failed>{exploreJob.error}</StatusMsg>}
      </Card>

      {/* Step 2 — Download */}
      <Card>
        <StepLabel>Step 2 — Download Reports</StepLabel>
        <CardTitle>Data Integration</CardTitle>
        <CardDesc>
          For each patient, clicks through to their MARCoNS report and downloads the PDF.
          Files are saved to <code>~/Downloads/drschmoeai_imports/microbiologydx/microbiologydx_page_NN/</code>.
          {totalPages === 0 && <span style={{ color: '#92400e' }}> Run Explore first to detect page count.</span>}
        </CardDesc>

        {totalPages > 0 && (
          <PageSelectorWrap>
            <PageSelectorLabel>Pages to download</PageSelectorLabel>
            <PageModeRow>
              <PageModeBtn $active={pageMode === 'all'} onClick={() => setPageMode('all')}>
                All {totalPages} pages
              </PageModeBtn>
              <PageModeBtn $active={pageMode === 'select'} onClick={() => setPageMode('select')}>
                Select specific pages
              </PageModeBtn>
            </PageModeRow>
            {pageMode === 'select' && (
              <>
                <PageGrid>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <PageChip key={n} $selected={selectedPages.has(n)} onClick={() => togglePage(n)}>{n}</PageChip>
                  ))}
                </PageGrid>
                <StatusMsg style={{ marginTop: 8 }}>
                  {selectedPages.size === 0 ? 'Select at least one page.' : `${selectedPages.size} page${selectedPages.size !== 1 ? 's' : ''} selected`}
                </StatusMsg>
              </>
            )}
          </PageSelectorWrap>
        )}

        <GuidedRow>
          <input type="checkbox" checked={guidedMode} onChange={(e) => setGuidedMode(e.target.checked)} />
          <span>
            <strong>Guided mode</strong> — show a status banner on the MicrobiologyDx tab, highlight each
            button, and use slower pacing. Turn off for a faster crawl.
          </span>
        </GuidedRow>

        <div style={{ marginTop: 16 }}>
          <ActionBtn $running={crawlerRunning} disabled={!canDownload} onClick={handleStartCrawler}>
            {crawlerStarting ? 'Starting...' : crawlerRunning ? 'Download Running...' : 'Start Download'}
          </ActionBtn>
        </div>

        {crawlerError && <StatusMsg $failed>{crawlerError}</StatusMsg>}

        {crawlerJob && (
          <ProgressSection>
            <ProgressLabel>
              <span>
                {crawlerJob.status === 'completed' ? '✓ Complete' :
                 crawlerJob.status === 'failed'    ? '✗ Failed' :
                 `Page ${crawlerJob.current_page} of ${crawlerJob.total_pages}`}
                {crawlerJob.downloaded_files > 0 && ` · ${crawlerJob.downloaded_files} PDFs`}
              </span>
              <span>{crawlerJob.percent.toFixed(1)}%</span>
            </ProgressLabel>
            <ProgressTrack>
              <ProgressFill $pct={crawlerJob.percent} $done={crawlerJob.status === 'completed'} $failed={crawlerJob.status === 'failed'} />
            </ProgressTrack>
            <StatusMsg $failed={crawlerJob.status === 'failed'}>
              {crawlerJob.status === 'failed' ? crawlerJob.error : crawlerJob.message}
            </StatusMsg>
          </ProgressSection>
        )}

        {crawlerHistory.length > 0 && (
          <ProgressSection>
            <HistoryTable>
              <thead><tr><Th>Status</Th><Th>Progress</Th><Th>PDFs</Th><Th>Guided</Th><Th>Started</Th></tr></thead>
              <tbody>
                {crawlerHistory.map((job) => (
                  <tr key={job.job_id}>
                    <Td><StatusBadge $status={job.status}>{job.status}</StatusBadge></Td>
                    <Td>{job.percent.toFixed(1)}% ({job.current_page}/{job.total_pages})</Td>
                    <Td>{job.downloaded_files}</Td>
                    <Td>{job.guided_mode === false ? 'Off' : 'On'}</Td>
                    <Td>{new Date(job.created_at).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          </ProgressSection>
        )}
      </Card>
    </Container>
  );

  return <ModalityTabShell sourceVendor="microbiology_dx">{automationContent}</ModalityTabShell>;
};

// ── JaneApp Tab ───────────────────────────────────────────────────────────────

export const JaneAppTab: React.FC = () => {
  const [portalPrefix, setPortalPrefix]     = useState('');
  const [groupName, setGroupName]           = useState('');
  const [prefixLoaded, setPrefixLoaded]     = useState(false);
  const [exploreJob, setExploreJob]         = useState<PortalExploreResult | null>(null);
  const [exploring, setExploring]           = useState(false);
  const [exploreError, setExploreError]     = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [confirmingLogin, setConfirmingLogin] = useState(false);
  const explorePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [patientMode, setPatientMode]         = useState<'all' | 'select'>('all');
  const [selectedPatients, setSelectedPatients] = useState<Set<number>>(new Set());
  const [crawlerJob, setCrawlerJob]           = useState<CrawlerJob | null>(null);
  const [crawlerHistory, setCrawlerHistory]   = useState<CrawlerJob[]>([]);
  const [crawlerStarting, setCrawlerStarting] = useState(false);
  const [crawlerError, setCrawlerError]       = useState<string | null>(null);
  const [guidedMode, setGuidedMode]           = useState(true);
  const [agentDownloading, setAgentDownloading] = useState(false);
  const crawlerPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopExplorePoll = useCallback(() => {
    if (explorePollRef.current) { clearInterval(explorePollRef.current); explorePollRef.current = null; }
  }, []);

  const pollExplore = useCallback(async (jobId: string) => {
    try {
      const res = await getExploreStatus(jobId);
      setExploreJob(res);
      if (res.status === 'completed' || res.status === 'failed') stopExplorePoll();
    } catch { /* blip */ }
  }, [stopExplorePoll]);

  const stopCrawlerPoll = useCallback(() => {
    if (crawlerPollRef.current) { clearInterval(crawlerPollRef.current); crawlerPollRef.current = null; }
  }, []);

  const pollCrawler = useCallback(async (jobId: string) => {
    try {
      const res = await getCrawlerJobStatus(jobId);
      setCrawlerJob(res);
      if (res.status === 'completed' || res.status === 'failed') {
        stopCrawlerPoll();
        getCrawlerJobs('janeapp').then((r) => setCrawlerHistory(r.jobs)).catch(() => {});
      }
    } catch { /* blip */ }
  }, [stopCrawlerPoll]);

  const startCrawlerPoll = useCallback((jobId: string) => {
    stopCrawlerPoll();
    crawlerPollRef.current = setInterval(() => pollCrawler(jobId), POLL_MS);
  }, [pollCrawler, stopCrawlerPoll]);

  useEffect(() => {
    getGroupSettings().then((res) => {
      if (res.success && res.data) {
        setPortalPrefix(res.data.jane_app_prefix || '');
        setGroupName(res.data.group_name || '');
      }
      setPrefixLoaded(true);
    }).catch(() => setPrefixLoaded(true));

    getLatestExploreResult('janeapp').then((res) => {
      if (res.success) setExploreJob(res);
    }).catch(() => {});
    getCrawlerJobs('janeapp').then((res) => {
      setCrawlerHistory(res.jobs);
      const running = res.jobs.find((j) => j.status === 'running' || j.status === 'pending');
      if (running) { setCrawlerJob(running); startCrawlerPoll(running.job_id); }
    }).catch(() => {});
    return () => { stopCrawlerPoll(); stopExplorePoll(); };
  }, [startCrawlerPoll, stopCrawlerPoll, stopExplorePoll]);

  const handleExplore = async () => {
    if (!portalPrefix.trim()) { setExploreError('Enter your JaneApp prefix first.'); return; }
    setShowLoginPrompt(false);
    setExploreError(null);
    setExploring(true);
    setExploreJob(null);
    try {
      const res = await explorePortal('janeapp', { portal_prefix: portalPrefix.trim() });
      if (!res.success || !res.job_id) { setExploreError(res.error || 'Failed to start.'); return; }
      const st = await getExploreStatus(res.job_id);
      setExploreJob(st);
      stopExplorePoll();
      explorePollRef.current = setInterval(() => pollExplore(res.job_id!), POLL_MS);
    } catch (e: any) {
      setExploreError(e?.message || 'Failed to connect.');
    } finally {
      setExploring(false);
    }
  };

  const handleStartCrawler = async () => {
    if (!portalPrefix.trim()) { setCrawlerError('Enter your JaneApp prefix first.'); return; }
    setCrawlerError(null);
    setCrawlerStarting(true);
    const pages = patientMode === 'select' && selectedPatients.size > 0
      ? Array.from(selectedPatients).sort((a, b) => a - b)
      : 'all';
    try {
      const res = await startCrawlerJob('janeapp', {
        guided_mode: guidedMode,
        pages,
        portal_prefix: portalPrefix.trim(),
      });
      if (!res.success || !res.job_id) { setCrawlerError(res.error || 'Failed to start.'); return; }
      const st = await getCrawlerJobStatus(res.job_id);
      setCrawlerJob(st);
      startCrawlerPoll(res.job_id);
    } catch (e: any) {
      setCrawlerError(e?.message || 'Failed to start.');
    } finally {
      setCrawlerStarting(false);
    }
  };

  const togglePatient = (id: number) => {
    setSelectedPatients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const patients = exploreJob?.meta?.patients || [];
  const totalPatients = exploreJob?.status === 'completed' ? (exploreJob.estimated_patients || patients.length) : 0;
  const exploreRunning = exploreJob?.status === 'running' || exploreJob?.status === 'pending' || exploring;
  const crawlerRunning = crawlerJob?.status === 'running' || crawlerJob?.status === 'pending';
  const canDownload    = !crawlerRunning && !crawlerStarting && totalPatients > 0 && portalPrefix.trim() !== ''
                         && (patientMode === 'all' || selectedPatients.size > 0);
  const portalUrl      = portalPrefix.trim() ? `https://${portalPrefix.trim()}.janeapp.com/admin` : '';

  const automationContent = (
    <Container>
      {/* Portal URL row */}
      <Card>
        <StepLabel>Setup — JaneApp Portal</StepLabel>
        <CardTitle>Your JaneApp URL</CardTitle>
        <CardDesc>
          Enter your JaneApp subdomain prefix. Your portal URL is{' '}
          <strong>{portalUrl || '[prefix].janeapp.com/admin'}</strong>.
          {!prefixLoaded ? '' : !portalPrefix && (
            <span style={{ color: '#92400e' }}> Set your prefix in <strong>Settings → Groups</strong> or enter it below.</span>
          )}
        </CardDesc>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, maxWidth: 440 }}>
          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden', flex: 1 }}>
            <span style={{ padding: '8px 10px', background: '#f3f4f6', color: '#6b7280', fontSize: 13, borderRight: '1px solid #d1d5db', whiteSpace: 'nowrap' }}>https://</span>
            <input
              type="text"
              placeholder="thefncminnesota"
              value={portalPrefix}
              onChange={(e) => setPortalPrefix(e.target.value)}
              style={{ flex: 1, padding: '8px 10px', border: 'none', outline: 'none', fontSize: 13, fontFamily: 'monospace' }}
              spellCheck={false}
            />
            <span style={{ padding: '8px 10px', background: '#f3f4f6', color: '#6b7280', fontSize: 13, borderLeft: '1px solid #d1d5db', whiteSpace: 'nowrap' }}>.janeapp.com/admin</span>
          </div>
        </div>
      </Card>

      {/* Step 1 — Explore */}
      <Card>
        <StepLabel>Step 1 — Explore Portal</StepLabel>
        <CardTitle>Preview</CardTitle>
        <CardDesc>
          Reads the JaneApp patient list to count patients — no downloads.
        </CardDesc>

        <SourceRow>
          <LogoImg src={janeAppLogo} alt="JaneApp" />
          <SourceInfo>
            <SourceName>JaneApp Admin</SourceName>
            <SourceSub>{portalUrl || '*.janeapp.com/admin'} · Read-only scan</SourceSub>
          </SourceInfo>
          <ActionBtn
            $variant="indigo"
            $running={exploreRunning}
            disabled={exploreRunning || !portalPrefix.trim()}
            onClick={() => setShowLoginPrompt(true)}
          >
            {exploreRunning ? 'Exploring...' : 'Explore Portal'}
          </ActionBtn>
        </SourceRow>

        <CdpNote>
          <strong>Requires the DrSchmoe Agent desktop app.</strong>
          <br />
          The Agent connects Chrome to your JaneApp admin portal automatically.
          <AgentDownloadRow>
            <DownloadBtn
              disabled={agentDownloading}
              onClick={() => {
                setAgentDownloading(true);
                getMcpAgentDownloadUrl()
                  .then((res) => { window.location.href = res.url; })
                  .catch(() => {})
                  .finally(() => setAgentDownloading(false));
              }}
            >
              {agentDownloading ? 'Preparing…' : '↓ Download DrSchmoe Agent (macOS)'}
            </DownloadBtn>
          </AgentDownloadRow>
        </CdpNote>

        {showLoginPrompt && !exploreRunning && (
          <LoginPrompt>
            <LoginPromptText>
              DrSchmoe Agent will open Chrome automatically.{' '}
              Log into <strong>{portalUrl}</strong> when Chrome opens, then click{' '}
              <strong>"I'm Logged In"</strong> to start the scan.
            </LoginPromptText>
            <LoginPromptActions>
              <ActionBtn $variant="indigo" $running={false} onClick={handleExplore}>
                Open Chrome &amp; Start Exploring
              </ActionBtn>
              <CancelBtn onClick={() => setShowLoginPrompt(false)}>Cancel</CancelBtn>
            </LoginPromptActions>
          </LoginPrompt>
        )}

        {exploreError && <StatusMsg $failed>{exploreError}</StatusMsg>}

        {exploreJob?.status === 'running' && exploreJob.message?.toLowerCase().includes('log into') && (
          <ConfirmLoginBanner>
            <ConfirmLoginText>
              Chrome is open — <strong>log into {portalUrl}</strong>, then click below.
            </ConfirmLoginText>
            <ConfirmLoginBtn
              onClick={async () => { setConfirmingLogin(true); try { await confirmMcpLogin(); } catch {} finally { setConfirmingLogin(false); } }}
              disabled={confirmingLogin}
            >
              {confirmingLogin ? 'Confirming...' : "I'm Logged In — Start Exploring"}
            </ConfirmLoginBtn>
          </ConfirmLoginBanner>
        )}

        {exploreJob?.status === 'running' && !exploreJob.message?.toLowerCase().includes('log into') && (
          <StatusMsg>{exploreJob.message || 'Scanning portal...'}</StatusMsg>
        )}

        {exploreJob?.status === 'completed' && (
          <StatsRow>
            <StatCard><StatValue>{totalPatients}</StatValue><StatLabel>Total patients</StatLabel></StatCard>
          </StatsRow>
        )}

        {exploreJob?.status === 'failed' && <StatusMsg $failed>{exploreJob.error}</StatusMsg>}
      </Card>

      {/* Step 2 — Download */}
      <Card>
        <StepLabel>Step 2 — Download Files</StepLabel>
        <CardTitle>Data Integration</CardTitle>
        <CardDesc>
          For each patient, opens their Files tab and downloads every PDF.
          Files are saved to <code>~/Downloads/{groupName ? groupName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') : '<your-group>'}/janeapp/janeapp_patient_NNNNN/</code>.
          {totalPatients === 0 && <span style={{ color: '#92400e' }}> Run Explore first to detect patients.</span>}
        </CardDesc>

        {patients.length > 0 && (
          <PageSelectorWrap>
            <PageSelectorLabel>Patients to download</PageSelectorLabel>
            <PageModeRow>
              <PageModeBtn $active={patientMode === 'all'} onClick={() => setPatientMode('all')}>
                All {totalPatients} patients
              </PageModeBtn>
              <PageModeBtn $active={patientMode === 'select'} onClick={() => setPatientMode('select')}>
                Select specific patients
              </PageModeBtn>
            </PageModeRow>
            {patientMode === 'select' && (
              <>
                <PageGrid>
                  {patients.map((pt) => (
                    <PageChip
                      key={pt.id}
                      $selected={selectedPatients.has(pt.id)}
                      onClick={() => togglePatient(pt.id)}
                      title={`#${pt.id}`}
                    >
                      {pt.name}
                    </PageChip>
                  ))}
                </PageGrid>
                <StatusMsg style={{ marginTop: 8 }}>
                  {selectedPatients.size === 0
                    ? 'Select at least one patient.'
                    : `${selectedPatients.size} patient${selectedPatients.size !== 1 ? 's' : ''} selected`}
                </StatusMsg>
              </>
            )}
          </PageSelectorWrap>
        )}

        <GuidedRow>
          <input type="checkbox" checked={guidedMode} onChange={(e) => setGuidedMode(e.target.checked)} />
          <span>
            <strong>Guided mode</strong> — show a status banner on the JaneApp tab, highlight each
            button, and use slower pacing. Turn off for a faster crawl.
          </span>
        </GuidedRow>

        <div style={{ marginTop: 16 }}>
          <ActionBtn $running={crawlerRunning} disabled={!canDownload} onClick={handleStartCrawler}>
            {crawlerStarting ? 'Starting...' : crawlerRunning ? 'Download Running...' : 'Start Download'}
          </ActionBtn>
        </div>

        {crawlerError && <StatusMsg $failed>{crawlerError}</StatusMsg>}

        {crawlerJob && (
          <ProgressSection>
            <ProgressLabel>
              <span>
                {crawlerJob.status === 'completed' ? '✓ Complete' :
                 crawlerJob.status === 'failed'    ? '✗ Failed' :
                 `Patient ${crawlerJob.current_page} of ${crawlerJob.total_pages}`}
                {crawlerJob.downloaded_files > 0 && ` · ${crawlerJob.downloaded_files} files`}
              </span>
              <span>{crawlerJob.percent.toFixed(1)}%</span>
            </ProgressLabel>
            <ProgressTrack>
              <ProgressFill $pct={crawlerJob.percent} $done={crawlerJob.status === 'completed'} $failed={crawlerJob.status === 'failed'} />
            </ProgressTrack>
            <StatusMsg $failed={crawlerJob.status === 'failed'}>
              {crawlerJob.status === 'failed' ? crawlerJob.error : crawlerJob.message}
            </StatusMsg>
          </ProgressSection>
        )}

        {crawlerHistory.length > 0 && (
          <ProgressSection>
            <HistoryTable>
              <thead><tr><Th>Status</Th><Th>Progress</Th><Th>Files</Th><Th>Guided</Th><Th>Started</Th></tr></thead>
              <tbody>
                {crawlerHistory.map((job) => (
                  <tr key={job.job_id}>
                    <Td><StatusBadge $status={job.status}>{job.status}</StatusBadge></Td>
                    <Td>{job.percent.toFixed(1)}% ({job.current_page}/{job.total_pages})</Td>
                    <Td>{job.downloaded_files}</Td>
                    <Td>{job.guided_mode === false ? 'Off' : 'On'}</Td>
                    <Td>{new Date(job.created_at).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          </ProgressSection>
        )}
      </Card>
    </Container>
  );

  return <ModalityTabShell sourceVendor="janeapp">{automationContent}</ModalityTabShell>;
};

// ── Main component with sub-tabs ──────────────────────────────────────────────

const SUBTABS = [
  { id: 'vibrant', label: 'Vibrant Wellness' },
  { id: 'rupa',    label: 'Rupa Health' },
  { id: 'labcorp', label: 'LabCorp' },
] as const;

type SubTabId = (typeof SUBTABS)[number]['id'];

const BloodTestIntegration: React.FC = () => {
  const [subTab, setSubTab] = useState<SubTabId>('vibrant');

  return (
    <div>
      <SubTabBar>
        {SUBTABS.map((t) => (
          <SubTab key={t.id} $active={subTab === t.id} onClick={() => setSubTab(t.id)}>
            {t.label}
          </SubTab>
        ))}
      </SubTabBar>

      {subTab === 'vibrant' && <VibrantTab />}
      {subTab === 'rupa'    && <RupaTab />}
      {subTab === 'labcorp' && (
        <Placeholder>LabCorp integration — coming soon.</Placeholder>
      )}
    </div>
  );
};

export default BloodTestIntegration;
